import { defineComponent as _defineComponent } from 'vue'
import { computed, useAsync, useRoute, useStore, watch } from "@nuxtjs/composition-api";

import { useAmplitude, useConfig, useLinkService } from "@/composables";
import { useConsumerSearchStore } from "@/store";
import { getAmplitudeStore } from "@/store/amplitude";
import { getExperimentsStore } from "@/store/experiments";
import { getLtksStore } from "@/store/ltks";
import { getProfilesStore } from "@/store/profiles";
import { getSearchStore } from "@/store/search";
import { EXPERIMENTS_CONSUMER_SEARCH, FEATURE_FLAG_ACTIVE } from "@/utils/experiments/constants";
import { Actions } from "@/utils/snowplow/enums";
import { createPostImpression } from "@/utils/snowplow/factory";


export default /*#__PURE__*/_defineComponent({
  __name: 'LtkHeroImage',
  props: {
  ltkId: {
    type: String,
    required: true,
  },
  position: {
    type: Number,
    required: false,
    default: undefined,
  },
  height: {
    type: Number,
    required: false,
    default: undefined,
  },
  width: {
    type: Number,
    required: false,
    default: undefined,
  },
  defaultWidth: {
    type: Number,
    required: false,
    default: 450,
  },
  rounded: {
    type: Boolean,
    required: false,
    default: true,
  },
  hasLink: {
    type: Boolean,
    required: false,
    default: true,
  },
  fillWidth: {
    type: Boolean,
    required: false,
    default: false,
  },
  isVideo: {
    type: Boolean,
    required: false,
    default: false,
  },
  outlined: {
    type: Boolean,
    required: false,
    default: false,
  },
  referringComponent: {
    type: String,
    required: false,
    default: "",
  },
  experiment: {
    type: Boolean,
    default: false,
  },
},
  setup(__props) {

const props = __props;



const amplitude = useAmplitude();
const config = useConfig();
const store = useStore();
const route = useRoute();
const linkService = useLinkService();
const experimentsStore = getExperimentsStore(store);
const searchStore = getSearchStore(store);
const amplitudeStore = getAmplitudeStore(store);
const consumerSearchStore = useConsumerSearchStore();

const isConsumerSearchEnabled = computed(
  () => experimentsStore.variant(EXPERIMENTS_CONSUMER_SEARCH).value === FEATURE_FLAG_ACTIVE,
);
const isSearchPage = computed(() => route.value.name === "search");

const fetch = () => getLtksStore(store).fetchLtkByIdAggregate({ id: props.ltkId });

useAsync(() => fetch(), "LtkHeroImage");

const ltk = computed(() => {
  const ltkStore = getLtksStore(store);
  return ltkStore.ltks[props.ltkId];
});

const displayName = computed(() => {
  const profileStore = getProfilesStore(store);
  return profileStore.getDisplayNameById(ltk.value?.profileId);
});

const aspectRatio = computed(() => {
  const ltkStore = getLtksStore(store);
  return props.width !== undefined && props.height !== undefined
    ? props.width / props.height
    : ltkStore.ltkImageAspectRatio(props.ltkId);
});

const heroImageLink = computed(() => {
  if (!ltk.value?.heroImage) {
    return "";
  }

  if (props.height !== undefined && props.width !== undefined) {
    return linkService.getImageLink({
      url: ltk.value?.heroImage ?? "",
      width: calculatedWidth.value.toString(),
      height: calculatedHeight.value.toString(),
      useDefaultDpr: false,
    });
  }

  return linkService.getImageLink({
    url: ltk.value?.heroImage ?? "",
    width: props.defaultWidth.toString(),
    crop: false,
    useDefaultDpr: false,
  });
});

const heroImageSrcSet = computed(() =>
  ltk.value?.heroImage
    ? `${heroImageLink.value}&dpr=1 1x, ${heroImageLink.value}&dpr=2 2x, ${heroImageLink.value}&dpr=3 3x`
    : "",
);

const calculatedWidth = computed(() => {
  if (props.width !== undefined) {
    return props.width;
  }

  if (props.width === undefined && props.height !== undefined) {
    return props.height * aspectRatio.value;
  }

  return props.defaultWidth;
});

const calculatedHeight = computed(() =>
  props.height ? props.height : calculatedWidth.value * (1 / aspectRatio.value),
);

const onHeroClick = () => {
  if (props.referringComponent) {
    amplitudeStore.setReferringComponent(props.referringComponent);
  }
  if (isSearchPage.value) {
    searchAnalytics();
  }
};

const searchAnalytics = () => {
  const isPostSearch = route.value.query.type.toString() === "post";
  amplitude?.sendExitEvent("/search/tap", {
    "click-position": props.position + 1,
    "content-id": isPostSearch ? props.ltkId : ltk.value?.profileId,
    "content-type": isPostSearch ? "post" : "profile",
    index: `${config.env}-ltk-${isPostSearch ? "post-products" : "creator-profiles"}`,
    page: "search-results",
    "referring-component": props.referringComponent,
    "search-experience": isPostSearch ? "matching-posts-search" : "matching-creator-search",
  });
  amplitude?.sendExitEvent("/ltk-search-summary", {
    "referring-component": props.referringComponent,
    ...(isConsumerSearchEnabled.value
      ? {
          "post-count": consumerSearchStore.ltks.length,
          "profile-count": consumerSearchStore.profiles.length,
        }
      : {
          "post-count": searchStore.searchTypeItem("post").active.total,
          "profile-count": searchStore.searchTypeItem("profile").active.total,
        }),
    query: route.value.query.keyword.toString(),
  });
  // If consumer search is enabled, do not send search insights feedback to Alogolia
  if (isConsumerSearchEnabled) {
    return;
  }
  // Else, send search insights feedback to Algolia
  let position = -1;
  if (route.value.query.type === "post") {
    // If this is a "post" search, we can pull the index from the LTK store
    position = searchStore.searchTypeItem("post").active.ids.findIndex((id) => id === props.ltkId);
  } else {
    // If this is a "profile" search we can pull the index from the Profile store
    position = searchStore
      .searchResultsProfileIds("profile")
      .findIndex((profileId) => profileId === ltk.value?.profileId);
  }
  searchStore.resultClick({
    type: route.value.query.type as SearchType,
    event: "Search Result Click",
    objectId: props.ltkId,
    position,
    rootReferrer: amplitudeStore.rootReferrer ?? "",
    searchExperience: `Search-${route.value.query.type}`,
  });
};

const postImpressionPayload = computed(() =>
  createPostImpression(Actions.View, {
    id: props.ltkId,
    creator_id: ltk.value?.profileId ?? "",
    product_ids: ltk.value?.productIds,
    position: props.position,
  }),
);

watch(
  () => props.ltkId,
  () => {
    fetch();
  },
);

return { __sfc: true,props, amplitude, config, store, route, linkService, experimentsStore, searchStore, amplitudeStore, consumerSearchStore, isConsumerSearchEnabled, isSearchPage, fetch, ltk, displayName, aspectRatio, heroImageLink, heroImageSrcSet, calculatedWidth, calculatedHeight, onHeroClick, searchAnalytics, postImpressionPayload }
}

})